import CancelIcon from "@mui/icons-material/Cancel";
import ClearIcon from "@mui/icons-material/Clear";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import {
  Autocomplete,
  Chip,
  CircularProgress,
  IconButton,
  TextField,
} from "@mui/material";
import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getMethod2 } from "../../functions/httpMethods";
import useElementDimensions from "../../hooks/ElementDimensions";
import LightTooltip from "../general/LightTooltip";
import NunitoText from "../general/NunitoText";

const chipStyle = { backgroundColor: "#F16774", color: "#FFF" };

// Custom checkbox component matching the original styling
const CustomCheckbox = ({ isSelected }) => {
  CustomCheckbox.propTypes = {
    isSelected: PropTypes.bool,
  };
  const checkboxStyle = {
    height: 18,
    width: 18,
    borderRadius: 3,
    marginLeft: 10,
    backgroundColor: isSelected && "#000",
    border: "2px solid #000",
  };
  const checkmarkStyle = {
    position: "relative",
    right: 12,
    bottom: 1,
    width: 6,
    height: 12,
    border: "2px solid white",
    borderWidth: "0 2px 2px 0",
    transform: "rotate(45deg)",
    WebkitTransform: "rotate(45deg)",
    msTransform: "rotate(45deg)",
    visibility: !isSelected && "hidden",
  };
  return (
    <>
      <span style={checkboxStyle} />
      <span style={checkmarkStyle} />
    </>
  );
};

// Option component for multiple selection
const OptionForMultiple = ({
  option,
  name,
  isSelected,
  onChangeStateMultiple,
}) => {
  OptionForMultiple.propTypes = {
    option: PropTypes.number,
    name: PropTypes.string,
    isSelected: PropTypes.bool,
    onChangeStateMultiple: PropTypes.func,
  };
  const [color, setColor] = useState("#FFF");
  const containerStyle = {
    height: 52,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    backgroundColor: isSelected ? "#F0F8FF" : color,
  };
  return (
    <div
      style={containerStyle}
      onClick={() => onChangeStateMultiple(option)}
      onMouseEnter={() => setColor("#F2F2F2")}
      onMouseLeave={() => setColor("#FFF")}
    >
      <CustomCheckbox isSelected={isSelected} />
      {name}
    </div>
  );
};

// Multiple items display in the input field
const MultipleItems = ({
  state,
  dimensions,
  passedRef,
  options,
  onChangeStateMultiple,
  placeholder,
}) => {
  MultipleItems.propTypes = {
    state: PropTypes.array,
    dimensions: PropTypes.object,
    passedRef: PropTypes.object,
    options: PropTypes.array,
    onChangeStateMultiple: PropTypes.func,
    placeholder: PropTypes.string,
  };
  const { width } = dimensions;
  const flexStyle = { display: "flex" };
  const itemsInMultipleDivStyle = {
    paddingLeft: 10,
    display: "flex",
    overflow: "hidden",
    minWidth: 0,
    maxWidth: width - 250,
  };
  return (
    <div style={flexStyle}>
      <div style={itemsInMultipleDivStyle} ref={passedRef}>
        {state.map((s, i) => (
          <ItemInMultiple
            key={i}
            id={s}
            options={options}
            onChangeStateMultiple={onChangeStateMultiple}
          />
        ))}
      </div>
      <LightTooltip
        rollover={1}
        topic={0}
        hide={0}
        title={
          <BehindItems
            state={state}
            placeholder={placeholder}
            options={options}
          />
        }
      >
        <div style={{ paddingLeft: 10 }}>...</div>
      </LightTooltip>
    </div>
  );
};

const ItemInMultiple = ({ id, options, onChangeStateMultiple }) => {
  ItemInMultiple.propTypes = {
    id: PropTypes.number,
    options: PropTypes.array,
    onChangeStateMultiple: PropTypes.func,
  };
  return (
    <Chip
      style={{ ...chipStyle, marginRight: 10, paddingLeft: 20 }}
      label={options.find((t) => t.id === id)?.name || ""}
      onDelete={() => onChangeStateMultiple(id)}
      deleteIcon={
        <CancelIcon style={{ color: "#FFF", position: "absolute", left: 10 }} />
      }
    />
  );
};

const behindItemDivStyle = { minWidth: 150 };
const BehindItems = ({ state, placeholder, options }) => {
  BehindItems.propTypes = {
    state: PropTypes.array,
    placeholder: PropTypes.string,
    options: PropTypes.array,
  };
  return (
    <div style={behindItemDivStyle}>
      <NunitoText
        value={placeholder.replace("*", "")}
        fontSize={18}
        color="#000"
      />
      {state.map((s, i) => (
        <div key={i} style={{ display: "flex", justifyContent: "center" }}>
          <NunitoText
            value={options.find((t) => t.id === s)?.name}
            fontSize={16}
          />
        </div>
      ))}
    </div>
  );
};

/**
 * PaginatedDropDown Component
 *
 * A dropdown component with infinite scroll pagination and support for single/multiple selection
 *
 * @param {string} apiEndpoint - The API endpoint to fetch data from (e.g., 'tag/filter-options')
 * @param {boolean} multiple - Enable multiple selection mode
 * @param {array} state - Selected value(s) - array of IDs for multiple, single ID for single selection
 * @param {function} setState - Function to update state (target, value) => {} or (count, head, target, value) => {}
 * @param {string} target - Target identifier for setState callback
 * @param {number} count - Count for Redux state management (optional)
 * @param {string} head - Head for Redux state management (optional)
 * @param {string} placeholder - Placeholder text
 * @param {number} width - Component width (default: 610)
 * @param {number} height - Component height (default: 60)
 * @param {string} borderColor - Border color (default: '#707070')
 * @param {string} bgColor - Background color
 * @param {boolean} disabled - Disable the component
 * @param {boolean} grayedOut - Gray out the component
 * @param {boolean} readOnly - Make component read-only
 * @param {array} initialSelectedItems - Pre-selected items for edit mode [{id, name}, ...]
 */
export default function PaginatedDropDown({
  apiEndpoint,
  multiple = false,
  state,
  setState,
  target,
  count,
  head,
  placeholder = "Select...",
  width = 610,
  height = 60,
  borderColor = "#707070",
  bgColor,
  disabled = false,
  grayedOut = false,
  readOnly = false,
  initialSelectedItems = [],
  extraParams = "",
  prependOptions = [],
}) {
  const [options, setOptions] = useState([]);
  const [input, setInput] = useState("");
  const [debouncedInput, setDebouncedInput] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [hasInitialFetch, setHasInitialFetch] = useState(false);

  const selfRef = useRef();
  const passedRef = useRef();
  const listboxRef = useRef(null);
  const dimensions = useElementDimensions(selfRef);
  const observerRef = useRef(null);
  const lastInputRef = useRef(debouncedInput); // Track last debounced input value
  const selectedItemsCache = useRef([]); // Cache selected items with their names
  const debounceTimerRef = useRef(null);

  // Debounced input effect with 500ms delay
  useEffect(() => {
    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer
    debounceTimerRef.current = setTimeout(() => {
      setDebouncedInput(input);
    }, 500);

    // Cleanup function
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [input]);

  // Initialize options with pre-selected items
  useEffect(() => {
    if (initialSelectedItems.length > 0) {
      setOptions((prevOptions) => {
        const existingIds = prevOptions.map((opt) => opt.id);
        const newItems = initialSelectedItems.filter(
          (item) => !existingIds.includes(item.id)
        );
        // Add to cache
        selectedItemsCache.current = [
          ...selectedItemsCache.current,
          ...newItems,
        ];
        return [...newItems, ...prevOptions];
      });
    }
  }, [initialSelectedItems]);

  // Update cache when items are selected and ensure they're in options
  useEffect(() => {
    const stateArray = Array.isArray(state) ? state : state ? [state] : [];

    setOptions((prevOptions) => {
      // Find items that are newly selected (in state but not in cache)
      const cachedIds = selectedItemsCache.current.map((item) => item.id);
      const newlySelectedIds = stateArray.filter(
        (id) => !cachedIds.includes(id)
      );

      // Get newly selected items from current options
      const newlySelectedItems = prevOptions.filter((opt) =>
        newlySelectedIds.includes(opt.id)
      );

      // Add newly selected items to cache
      if (newlySelectedItems.length > 0) {
        selectedItemsCache.current = [
          ...selectedItemsCache.current,
          ...newlySelectedItems,
        ];
      }

      // Remove deselected items from cache (items in cache but not in state)
      selectedItemsCache.current = selectedItemsCache.current.filter((item) =>
        stateArray.includes(item.id)
      );

      // Find selected items that are missing from current options
      const existingIds = prevOptions.map((opt) => opt.id);
      const missingIds = stateArray.filter((id) => !existingIds.includes(id));

      // Get missing items from cache
      const missingFromCache = missingIds
        .map((id) => selectedItemsCache.current.find((item) => item.id === id))
        .filter(Boolean);

      if (missingFromCache.length > 0) {
        // Add missing items to the beginning of options
        return [...missingFromCache, ...prevOptions];
      }

      return prevOptions;
    });
  }, [state]);

  // Fetch data from API
  const fetchData = useCallback(
    async (page = 1, searchQuery = "") => {
      if (page === 1) {
        setLoading(true);
        setHasInitialFetch(true);
      } else {
        setIsFetchingMore(true);
      }

      try {
        const params = `?page=${page}${
          searchQuery ? `&search=${searchQuery}` : ""
        }${extraParams ? `&${extraParams}` : ""}`;
        const response = await getMethod2(apiEndpoint, params);

        // Handle two API response shapes:
        // Shape A (e.g. tag/filter-options): { data: { data: [...], last_page: X } }
        // Shape B (e.g. school/list):        { data: [...], last_page: X }
        if (response) {
          let newData, lastPage;
          if (Array.isArray(response.data)) {
            newData = response.data;
            lastPage = response.last_page || 1;
          } else if (response.data) {
            newData = response.data.data || [];
            lastPage = response.data.last_page || 1;
          } else {
            newData = [];
            lastPage = 1;
          }

          setOptions((prevOptions) => {
            // Get currently selected items from state
            const stateArray = Array.isArray(state)
              ? state
              : state
              ? [state]
              : [];

            if (page === 1) {
              // For first page, merge with cached selected items
              // Get all selected items from cache
              const cachedSelectedItems = selectedItemsCache.current.filter(
                (item) => stateArray.includes(item.id)
              );

              const allSelectedIds = cachedSelectedItems.map((item) => item.id);

              // Filter out selected items from new data
              const filteredNewData = newData.filter(
                (item) => !allSelectedIds.includes(item.id)
              );

              // Return cached selected items first, then new data
              return [...cachedSelectedItems, ...filteredNewData];
            } else {
              // For subsequent pages, avoid duplicates
              const existingIds = prevOptions.map((opt) => opt.id);
              const filteredNewData = newData.filter(
                (item) => !existingIds.includes(item.id)
              );
              return [...prevOptions, ...filteredNewData];
            }
          });

          setHasMore(page < lastPage);
          setCurrentPage(page);
        }
      } catch (error) {
        // Error fetching paginated data
      } finally {
        setLoading(false);
        setIsFetchingMore(false);
      }
    },
    [apiEndpoint, initialSelectedItems, state, extraParams]
  );

  // Initial data fetch when dropdown opens
  useEffect(() => {
    if (open && !hasInitialFetch) {
      fetchData(1, debouncedInput);
    }
  }, [open, fetchData, debouncedInput, hasInitialFetch]);

  // Handle search input change with debounced value
  useEffect(() => {
    // Only trigger if debounced input actually changed and we're open
    if (open && debouncedInput !== lastInputRef.current) {
      lastInputRef.current = debouncedInput;
      setOptions([]);
      setCurrentPage(1);
      setHasMore(true);
      setHasInitialFetch(false); // Reset to allow refetch
      fetchData(1, debouncedInput);
    }
  }, [debouncedInput, open, fetchData]);

  // Intersection observer for infinite scroll
  useEffect(() => {
    if (!listboxRef.current || !hasMore || isFetchingMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isFetchingMore) {
          fetchData(currentPage + 1, input);
        }
      },
      { threshold: 0.1 }
    );

    observerRef.current = observer;

    // Observe the last item in the list
    const listItems = listboxRef.current.querySelectorAll('[role="option"]');
    if (listItems.length > 0) {
      observer.observe(listItems[listItems.length - 1]);
    }

    return () => {
      if (observer) observer.disconnect();
    };
  }, [options, hasMore, isFetchingMore, currentPage, input, fetchData]);

  // Merge static prependOptions (e.g. "Home School") with fetched options,
  // deduplicating by id so that scrolling in more data never creates duplicates.
  const displayOptions = useMemo(() => {
    if (!prependOptions || prependOptions.length === 0) return options;
    const existingIds = new Set(options.map((o) => o.id));
    const filtered = prependOptions.filter((o) => !existingIds.has(o.id));
    return [...filtered, ...options];
  }, [options, prependOptions]);

  const expand = () => !readOnly && setOpen(true);
  const collapse = () => !readOnly && setOpen(false);
  const toggle = () => !readOnly && setOpen(!open);

  const onChangeState = (value) => {
    if (count !== undefined && head !== undefined && target !== undefined) {
      // For Redux autocompletes with count, head, and target
      return setState(count, head, target, value);
    }
    if (count !== undefined && target !== undefined) {
      // For states in an array of objects
      return setState(count, target, value);
    }
    // For standard use with target only
    if (target !== undefined) {
      setState(target, value);
    } else {
      setState(value);
    }
  };

  const onChangeInput = (e, value) => {
    if (e && e.type === "change") {
      setInput(value);
      // Don't clear selected items when input is cleared - only update search
    }
  };

  const onChangeStateMultiple = (value) => {
    if (readOnly) return;
    let newState = [...state];

    if (newState.includes(value)) {
      newState = newState.filter((f) => f !== value);
    } else {
      newState.push(value);
    }

    onChangeState(newState);
  };

  const onChangeSingle = (e, value) => {
    if (value && typeof value === "object") {
      onChangeState(value.id ?? "");
    } else {
      onChangeState(value || "");
    }
  };

  const defaultBorder = borderColor
    ? `1px solid ${borderColor}`
    : bgColor
    ? `1px solid ${bgColor}`
    : "none";

  const searchIconStyle = {
    fontSize: 30,
    color: "rgb(112, 112, 112, 0.5)",
    marginLeft: 12,
  };

  const autoCompleteStyle = {
    width,
    height,
    visibility: "visible",
    pointerEvents: readOnly && "none",
  };

  const textFieldStyle = {
    height,
    border: defaultBorder,
    borderRadius: 12,
    backgroundColor: grayedOut ? "#707070" : bgColor,
  };

  const inputStyle = {
    marginLeft: 20,
    color: grayedOut && "#FFF",
    cursor: readOnly && "context-menu",
  };

  const btnStyle = {
    color: "#FFF",
    backgroundColor: "#144A94",
    borderRadius: "0 10px 10px 0",
    height: height - 2,
    width: 64,
    opacity: grayedOut && 0.5,
  };

  const iconStyle = { fontSize: 42 };

  const textFieldInputProps = (params) => ({
    disableUnderline: true,
    style: textFieldStyle,
    startAdornment: (
      <>
        {multiple && params.InputProps.startAdornment}
        <SearchIcon style={searchIconStyle} />
      </>
    ),
    endAdornment: (
      <>
        {(loading || isFetchingMore) && (
          <CircularProgress disableShrink style={{ color: "#000" }} size={20} />
        )}
        {multiple && (
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              onChangeState([]);
            }}
            disabled={readOnly}
          >
            <ClearIcon
              style={{ backgroundColor: grayedOut ? "#707070" : bgColor }}
            />
          </IconButton>
        )}
        <IconButton onClick={() => toggle()} style={btnStyle}>
          {open ? (
            <ExpandLessIcon style={iconStyle} />
          ) : (
            <ExpandMoreIcon style={iconStyle} />
          )}
        </IconButton>
      </>
    ),
  });

  const autoCompleteAttributes = {
    ref: selfRef,
    open: open,
    onClose: () => collapse(),
    onOpen: () => expand(),
    disableClearable: true,
    forcePopupIcon: false,
    disabled,
    style: autoCompleteStyle,
    loading: loading,
    options: displayOptions,
    getOptionLabel: (option) => {
      if (typeof option === "object") return option.name || "";
      const found = displayOptions.find((o) => o.id === option);
      return found ? found.name : "";
    },
    isOptionEqualToValue: (option, value) => {
      if (typeof option === "object" && typeof value === "object") {
        return option.id === value.id;
      }
      return option === value;
    },
    filterOptions: (x) => x, // Disable default filtering since we handle it server-side
    ListboxProps: {
      ref: listboxRef,
      style: { maxHeight: "400px" },
    },
  };

  const textFieldAttributes = {
    variant: "standard",
    placeholder,
  };

  if (multiple) {
    autoCompleteAttributes.multiple = true;
    autoCompleteAttributes.value = state;
    autoCompleteAttributes.inputValue = input; // Allow typing for search
    autoCompleteAttributes.onInputChange = onChangeInput; // Handle input changes
    autoCompleteAttributes.onChange = (e, newValue) => {
      onChangeState(newValue.map((v) => (typeof v === "object" ? v.id : v)));
      // Don't clear search - keep it so user can continue searching
    };
    autoCompleteAttributes.getOptionDisabled = (option) =>
      state.includes(option.id); // Disable already selected options
    autoCompleteAttributes.renderOption = (renderProps, option) => {
      const isSelected = state.includes(option.id);
      const isDisabled = state.includes(option.id);
      return (
        <li
          {...renderProps}
          key={option.id}
          style={{
            padding: 0,
            listStyle: "none",
            opacity: isDisabled ? 0.5 : 1,
            pointerEvents: isDisabled ? "none" : "auto",
          }}
        >
          <OptionForMultiple
            option={option.id}
            name={option.name}
            isSelected={isSelected}
            onChangeStateMultiple={onChangeStateMultiple}
          />
        </li>
      );
    };
    autoCompleteAttributes.renderTags = () => (
      <MultipleItems
        state={state}
        dimensions={dimensions}
        passedRef={passedRef}
        options={displayOptions}
        onChangeStateMultiple={onChangeStateMultiple}
        placeholder={placeholder}
      />
    );
    autoCompleteAttributes.disableCloseOnSelect = true;
    // Add loading indicator at the end
    autoCompleteAttributes.ListboxProps = {
      ...autoCompleteAttributes.ListboxProps,
      onScroll: (event) => {
        const listboxNode = event.currentTarget;
        if (
          listboxNode.scrollTop + listboxNode.clientHeight >=
          listboxNode.scrollHeight - 10
        ) {
          if (hasMore && !isFetchingMore) {
            fetchData(currentPage + 1, input);
          }
        }
      },
    };
  } else {
    // Single selection mode
    const selectedOption = state
      ? displayOptions.find((o) => o.id === state) || null
      : null;
    autoCompleteAttributes.value = selectedOption;
    autoCompleteAttributes.onChange = onChangeSingle;
    autoCompleteAttributes.onInputChange = onChangeInput;
    // When closed: show the selected option's label so pre-selected values are
    // visible on initial render. When open: show the typed search query.
    autoCompleteAttributes.inputValue =
      !open && selectedOption ? selectedOption.name : input;
    autoCompleteAttributes.selectOnFocus = true;
    autoCompleteAttributes.renderOption = (renderProps, option) => {
      return (
        <li
          {...renderProps}
          key={option.id}
          style={{
            padding: "10px 20px",
            cursor: "pointer",
            backgroundColor: state === option.id ? "#F0F8FF" : "transparent",
          }}
          onClick={() => {
            onChangeState(option.id);
            setInput(option.name);
            collapse();
          }}
        >
          {option.name}
        </li>
      );
    };
    // Add scroll handler for infinite scroll
    autoCompleteAttributes.ListboxProps = {
      ...autoCompleteAttributes.ListboxProps,
      onScroll: (event) => {
        const listboxNode = event.currentTarget;
        if (
          listboxNode.scrollTop + listboxNode.clientHeight >=
          listboxNode.scrollHeight - 10
        ) {
          if (hasMore && !isFetchingMore) {
            fetchData(currentPage + 1, input);
          }
        }
      },
    };
  }

  return (
    <Autocomplete
      {...autoCompleteAttributes}
      renderInput={(params) => (
        <TextField
          {...params}
          {...textFieldAttributes}
          InputProps={{
            ...params.InputProps,
            ...textFieldInputProps(params),
          }}
          inputProps={{
            ...params.inputProps,
            style: inputStyle,
            onKeyDown: (e) => {
              // Prevent backspace from removing chips when input is empty
              if (multiple && e.key === "Backspace" && input === "") {
                e.stopPropagation();
              }
            },
          }}
        />
      )}
    />
  );
}

PaginatedDropDown.propTypes = {
  apiEndpoint: PropTypes.string.isRequired,
  multiple: PropTypes.bool,
  state: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
    PropTypes.array,
  ]),
  setState: PropTypes.func.isRequired,
  target: PropTypes.string,
  count: PropTypes.number,
  head: PropTypes.string,
  placeholder: PropTypes.string,
  width: PropTypes.number,
  height: PropTypes.number,
  borderColor: PropTypes.string,
  bgColor: PropTypes.string,
  disabled: PropTypes.bool,
  grayedOut: PropTypes.bool,
  readOnly: PropTypes.bool,
  initialSelectedItems: PropTypes.array,
  extraParams: PropTypes.string,
  prependOptions: PropTypes.array,
};
